/* ========================================================================
   BACKTEST CHART PAGE  (namespaced – “mbc-”)
   2025-06-07
   ------------------------------------------------------------------------
   ▸ Performance-first: quick stats / meta load before the equity curve.
   ▸ The temporary “Strategy Performance” heading is gone forever.
   ▸ While stats are in-flight a spinner shows
       “Loading Strategy Performance…”.
   ▸ Header now renders
        ┌ Strategy Name (big)
        ├ Version N    (small badge, if provided)
        └ Date range
   ▸ Metrics grid and everything else stay exactly as before.
=========================================================================== */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation }          from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import Papa                                 from 'papaparse';
import './BacktestChartPage.css';
import IndicatorChart                        from '../components/IndicatorChart';
import useUser                               from '../hooks/useUser';
import { formatPercentWithThousands, formatNumberSmart } from '../utils/formatters';
import { detectResolutionFromData }         from '../utils/chartResolution';
import { isTerminalTradeEventStatus } from '../utils/tradeLifecycleStatus';
import { waitForApiToken } from '../utils/waitForApiToken';

/* ────────── format helpers (updated with thousand separators) ────────── */
const formatPercent = v => {
  if (v == null || isNaN(v)) return 'N/A';
  // Use 1 decimal for >= 10%, 2 decimals for < 10%
  const decimals = v * 100 >= 10 ? 1 : 2;
  return formatPercentWithThousands(v, decimals);
};
const formatNumber  = v => {
  if (v == null || isNaN(v)) return 'N/A';
  return formatNumberSmart(v);
};
const formatDate    = d =>
  !d ? '' : new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });

/* ──────────────────────────────────────────────────────────────
   CSV PARSERS – **FIXED** so they always resolve, ensuring the
   `chartLoading` flag flips to false and the “Loading chart data…”
   spinner never hangs indefinitely.
─────────────────────────────────────────────────────────────── */

/**
 * Parses terminal trade lifecycle events into the shape the Chart
 * component expects.  Resolves to an array even when CSV is empty.
 */
const parseTradesCSV = (csvText) =>
  new Promise((resolve, reject) => {
    try {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data }) => {
          try {
            const toNumber = (raw) => {
              if (raw === undefined || raw === null || raw === '') return undefined;
              const num = Number(raw);
              return Number.isFinite(num) ? num : undefined;
            };

            const mappedRows = data.reduce((acc, row) => {
              const status = (row.status || row.order_status || '').toLowerCase().trim();
              const side   = row.side?.toLowerCase();
              const price  = toNumber(row.price);

              // Include all trade sides from LumiBot Order.OrderSide enum
              const validSides = ['buy', 'sell', 'buy_to_open', 'buy_to_close', 'sell_to_open', 'sell_to_close', 'buy_to_cover', 'sell_short'];
              if (
                isTerminalTradeEventStatus(status) &&
                validSides.includes(side) &&
                price !== undefined
              ) {
                const tradeQuantity = toNumber(row.filled_quantity);
                const tradeCost     = toNumber(row.trade_cost);
                const assetStrike   = toNumber(row['asset.strike']);
                const assetMultiplier = toNumber(row['asset.multiplier']);
                const multiplier    = toNumber(row.multiplier);

                acc.push({
                  type   : 'trade',
                  time   : Math.floor(new Date(row.time).getTime() / 1000),
                  price,
                  side,
                  symbol : row.symbol || '',
                  status,
                  orderType: row.type || '',
                  strategy : row.strategy,
                  assetType: row['asset.asset_type'] || 'stock',
                  identifier: row.identifier,
                  assetRight: row['asset.right'],
                  assetStrike,
                  assetExpiration: row['asset.expiration'],
                  assetMultiplier,
                  multiplier,
                  tradeDetails: {
                    timeInForce  : row.time_in_force,
                    tradeQuantity,
                    tradeCost
                  }
                });
              }
              return acc;
            }, []);
            resolve(mappedRows);
          } catch (mapErr) {
            reject(mapErr);
          }
        },
        error: reject,
      });
    } catch (err) {
      reject(err);
    }
  });

/**
 * Parses indicator CSV (lines, markers, and OHLC) used by the Chart overlays.
 * Always resolves with an array (possibly empty) to prevent spinner hang.
 */
const parseIndicatorsCSV = (csvText) =>
  new Promise((resolve, reject) => {
    try {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data }) => {
          try {
            const grouped = {};

            data.forEach((row) => {
              const {
                datetime, timestamp, name, value,
                detail_text, color, symbol, type, style,
                // New asset columns for multi-symbol charting
                asset_symbol, asset_type, asset_expiration,
                asset_strike, asset_right, asset_multiplier,
                quote_symbol, asset_display_name,
                // OHLC columns
                open, high, low, close
              } = row;

              const t = timestamp
                ? Math.floor(parseFloat(timestamp))
                : Math.floor(new Date(datetime).getTime() / 1000);

              if (isNaN(t)) return;

              // Use asset_display_name if available, otherwise fall back to asset_symbol
              const assetKey = asset_display_name || asset_symbol || null;

              const rowType = (type ?? '').toLowerCase();

              /* OHLC series: aggregate points per name */
              if (rowType === 'ohlc') {
                const ohlcKey = `ohlc-${name}`;
                if (!grouped[ohlcKey]) {
                  grouped[ohlcKey] = {
                    type: 'ohlc',
                    name,
                    points: [],
                    // Asset info for chart overlay matching
                    asset_symbol: asset_symbol || null,
                    asset_display_name: asset_display_name || null,
                    asset_type: asset_type || null
                  };
                }
                grouped[ohlcKey].points.push({
                  time: t,
                  open: parseFloat(open),
                  high: parseFloat(high),
                  low: parseFloat(low),
                  close: parseFloat(close),
                  color: color || (parseFloat(close) >= parseFloat(open) ? 'green' : 'red'),
                  detail_text
                });
              }
              /* line-series: aggregate points per name */
              else if (rowType === 'line') {
                if (!grouped[name]) {
                  grouped[name] = {
                    type: 'line',
                    name,
                    color,
                    style,
                    points: [],
                    // Asset info for chart overlay matching
                    asset_symbol: asset_symbol || null,
                    asset_display_name: asset_display_name || null,
                    asset_type: asset_type || null
                  };
                }
                grouped[name].points.push({ time: t, value: parseFloat(value) });
              } else {
                /* markers / shapes */
                grouped[`${name}-${t}`] = {
                  type: (type ?? 'marker').toLowerCase(),
                  time: t,
                  value: parseFloat(value),
                  name,
                  detail_text,
                  color,
                  symbol,
                  // Asset info for chart overlay matching
                  asset_symbol: asset_symbol || null,
                  asset_display_name: asset_display_name || null,
                  asset_type: asset_type || null
                };
              }
            });

            /* tidy output */
            const indicators = Object.values(grouped).map((it) => {
              if (it.type === 'line' || it.type === 'ohlc') {
                it.points.sort((a, b) => a.time - b.time);
              }
              return it;
            });

            resolve(indicators);
          } catch (mapErr) {
            reject(mapErr);
          }
        },
        error: reject,
      });
    } catch (err) {
      reject(err);
    }
  });

export default function BacktestChartPage({ backtestId: propBacktestId, hideHeader = false, aiStrategyId: propAiStrategyId, revisionId: propRevisionId, backtestVersion: propBacktestVersion, strategyName: propStrategyName }) {
  const { getApiToken } = useUser();
  const { backtestId: paramBacktestId } = useParams();
  const location = useLocation();
  const backtestId = propBacktestId || paramBacktestId;

  // Extract aiStrategyId and revisionId from URL query params or props
  // These are needed to link "Ask AI" to the correct strategy conversation
  const { aiStrategyId, revisionId } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      aiStrategyId: propAiStrategyId || searchParams.get('aiStrategyId') || null,
      revisionId: propRevisionId || searchParams.get('revisionId') || null,
    };
  }, [location.search, propAiStrategyId, propRevisionId]);

  // Ref to stabilize getApiToken - prevents infinite re-render loops
  // when auth state changes cause getApiToken reference to change
  const getApiTokenRef = useRef(getApiToken);
  useEffect(() => { getApiTokenRef.current = getApiToken; }, [getApiToken]);

  /* ---------- quick-meta state ---------- */
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError,   setStatsError]   = useState('');
  const [backtest,     setBacktest]     = useState({
    strategyName : '',    // no placeholder text
    version      : null,
    startDate    : '',
    endDate      : '',
    metrics      : null
  });

  /* ---------- heavy chart state ---------- */
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError,   setChartError]   = useState('');
  const [indicatorData, setIndicatorData] = useState([]); // Line + OHLC indicators from indicators.csv
  const [signalData,   setSignalData]   = useState([]);   // Trade markers


  /* ---------- chart resolution state ---------- */
  // Resolution is auto-detected from indicator data timestamp gaps
  // This enables proper display of minute-level backtests vs daily
  const [chartResolution, setChartResolution] = useState({
    resolution: 'D',    // TradingView resolution string ('1', '5', '15', '60', '240', 'D', 'W')
    isIntraday: false   // Whether to preserve exact timestamps (true) or normalize to midnight (false)
  });


  /* =====================================================================
     1. QUICK-STATS fetch  (tiny payload → renders first)
  ===================================================================== */
  useEffect(() => {
    let on = true;
    (async () => {
      if (!backtestId) return;
      try {
        setStatsLoading(true); setStatsError('');
        const token = await waitForApiToken(() => getApiTokenRef.current());
        if (!on) return;
        if (!token) throw new Error('Authentication required');
        const res   = await fetch(
          `${process.env.REACT_APP_API_URL}/backtests/by-id/${backtestId}/stats`,
          { headers:{ Authorization:`Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const j  = await res.json();
        if (!on) return;

        const bt  = j.backtest ?? {};
        const ver = bt.version ?? bt.revisionVersion ??
                    bt.aiStrategyRevision?.version ?? null;

        setBacktest({
          strategyName : bt.strategyName || 'Strategy',
          version      : ver,
          startDate    : bt.startDate,
          endDate      : bt.endDate,
          metrics      : {
            annualReturn: bt.cagrAnnualReturn,
            maxDrawdown : bt.maxDrawdown,
            totalReturn : bt.totalReturn,
            sharpe      : bt.sharpe,
            sortino     : bt.sortino,
            romad       : bt.romad
          }
        });
      } catch {
        if (on) setStatsError('Failed to load metrics');
      } finally {
        if (on) setStatsLoading(false);
      }
    })();
    return () => { on = false; };
  }, [backtestId]); // Removed getApiToken - using ref instead to prevent re-render loops

  /* =====================================================================
     2. CHART / TRADES / INDICATORS fetch (heavier)

     NOTE: This chart now displays INDICATOR DATA from indicators.csv,
     NOT account value. The equity curve (account value) is shown
     separately in the EquityCurveChart component above.
  ===================================================================== */
  useEffect(() => {
    let on = true;
    (async () => {
      if (!backtestId) return;
      try {
        setChartLoading(true); setChartError('');
        const token = await waitForApiToken(() => getApiTokenRef.current());
        if (!on) return;
        if (!token) throw new Error('Authentication required');
        const res   = await fetch(
          `${process.env.REACT_APP_API_URL}/backtests/${backtestId}/result`,
          { headers:{ Authorization:`Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const j = await res.json();
        if (!on) return;

        // Parse trades (for markers) and indicators (for chart lines)
        const [trades, indicators] = await Promise.all([
          parseTradesCSV(j.files?.trades_csv || ''),
          j.files.indicators_csv ? parseIndicatorsCSV(j.files.indicators_csv) : []
        ]);

        // Separate indicators by type
        const lineIndicators = indicators.filter(ind => ind.type === 'line');
        const ohlcIndicators = indicators.filter(ind => ind.type === 'ohlc');
        const markerIndicators = indicators.filter(ind => ind.type !== 'line' && ind.type !== 'ohlc');

        console.log('[BacktestChartPage] Parsed indicators:', {
          lines: lineIndicators.map(l => l.name),
          ohlc: ohlcIndicators.map(o => o.name),
          markers: markerIndicators.length,
          trades: trades.length
        });

        // Set indicator data (line + OHLC series for chart)
        // IndicatorChart component handles separating lines from OHLC internally
        setIndicatorData([...lineIndicators, ...ohlcIndicators]);

        // Detect chart resolution from indicator data timestamp gaps
        // This is CRITICAL for minute-level backtests to display correctly
        // Without resolution detection, all data was collapsed to daily bars
        // Try line indicators first, then OHLC data for resolution detection
        const resolutionSourceData = lineIndicators.length > 0 && lineIndicators[0].points?.length >= 2
          ? lineIndicators[0].points
          : ohlcIndicators.length > 0 && ohlcIndicators[0].points?.length >= 2
            ? ohlcIndicators[0].points
            : null;

        if (resolutionSourceData) {
          const detectedResolution = detectResolutionFromData(resolutionSourceData);
          console.log('[BacktestChartPage] Detected chart resolution:', detectedResolution);
          setChartResolution({
            resolution: detectedResolution.resolution,
            isIntraday: detectedResolution.isIntraday
          });
        } else {
          console.log('[BacktestChartPage] Using default daily resolution (insufficient data for detection)');
          setChartResolution({ resolution: 'D', isIntraday: false });
        }

        // Set signal data (trades + markers for timescale marks)
        setSignalData([...trades, ...markerIndicators].sort((a,b) => a.time - b.time));
      } catch (err) {
        console.error('[BacktestChartPage] Error loading chart:', err);
        if (on) setChartError('Failed to load chart');
      } finally {
        if (on) setChartLoading(false);
      }
    })();
    return () => { on = false; };
  }, [backtestId]); // Removed getApiToken - using ref instead to prevent re-render loops


  /* ---------- small Metrics grid ---------- */
  const Metrics = () => {
    const m = backtest.metrics;
    if (!m) return null;
    return (
      <div className="metrics-summary">
        {[
          ['Annual Return', m.annualReturn, 'percent'],
          ['Max Drawdown',  m.maxDrawdown , 'percent'],
          ['Total Return',  m.totalReturn , 'percent'],
          ['Sharpe',        m.sharpe      , 'number'],
          ['Sortino',       m.sortino     , 'number'],
          ['ROMAD',         m.romad       , 'number']
        ].map(([lbl,val,fmt])=>(
          <div key={lbl} className="metric-item">
            <div className="metric-label">{lbl}</div>
            <div className={`metric-value ${
              fmt==='percent'
                ? (val>=0 ? 'positive' : 'negative')
                : (val>=0 ? 'positive' : 'neutral')
            }`}>
              {fmt==='percent' ? formatPercent(val) : formatNumber(val)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  /* =====================================================================
     RENDER
  ===================================================================== */
  return (
    <div className="backtest-chart-page">
        {/* ---------- HEADER (hidden when embedded) ---------- */}
        {!hideHeader && (
          <div className="chart-page-header">
            <div className="chart-page-title-container">
              <h1 className="chart-page-title">{backtest.strategyName}</h1>

              {backtest.version != null && (
                <div className="chart-strategy-name">
                  Version&nbsp;{backtest.version}
                </div>
              )}

              {backtest.startDate && backtest.endDate && (
                <div className="chart-date-range">
                  {formatDate(backtest.startDate)} – {formatDate(backtest.endDate)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------- QUICK METRICS (hidden when embedded) ---------- */}
        {!hideHeader && (
          statsLoading ? (
            <div className="chart-loading" style={{height:'auto'}}>
              <Spinner animation="border" size="sm"/>
              <p>Loading&nbsp;Strategy&nbsp;Performance…</p>
            </div>
          ) : statsError ? (
            <Alert variant="warning" className="chart-error">{statsError}</Alert>
          ) : (
            <Metrics />
          )
        )}

        {/* ---------- INDICATOR CHART ---------- */}
        {chartLoading && (
          <div className="chart-loading">
            <Spinner animation="border"/>
            <p>Loading chart data…</p>
          </div>
        )}

        {!chartLoading && chartError && (
          <Alert variant="danger" className="chart-error">{chartError}</Alert>
        )}

        {!chartLoading && !chartError && indicatorData.length > 0 && (
          <IndicatorChart
            indicatorData={indicatorData}
            signalData={signalData}
            resolution={chartResolution.resolution}
            isIntraday={chartResolution.isIntraday}
            height={800}
            key={chartResolution.resolution}
            aiStrategyId={aiStrategyId}
            revisionId={revisionId}
            backtestId={backtestId}
            backtestVersion={propBacktestVersion || backtest.version}
            strategyName={propStrategyName || backtest.strategyName}
          />
        )}

        {!chartLoading && !chartError && indicatorData.length === 0 && (
          <div className="chart-no-data">
            <p>No indicator data available for this backtest.</p>
          </div>
        )}
    </div>
  );
}
